import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { ContratoAnualizadoInput } from "@/lib/types";
import {
  actualizarContrato,
  crearContrato,
  darDeBajaContrato,
  listarContratos,
  obtenerContratoPorId,
  obtenerUsoContrato,
  reactivarContrato,
  type ListarContratosParams,
} from "../services/contratos-service";

/**
 * Listado PAGINADO server-side · los params (busqueda/page/pageSize) forman
 * parte de la key (local · extiende qk.adminContratosAnualizados para que los
 * invalidate de las mutations sigan alcanzando por prefijo).
 */
export const contratosAdminQueryOptions = (params: ListarContratosParams = {}) =>
  queryOptions({
    queryKey: [...qk.adminContratosAnualizados, "paginado", params] as const,
    queryFn: () => listarContratos(params),
    placeholderData: keepPreviousData,
  });

export const useContratosAdmin = (params: ListarContratosParams = {}) =>
  useQuery(contratosAdminQueryOptions(params));

/** Detalle de una VERSIÓN de contrato por su id (Guid) · para la ruta de edición. */
export const contratoAdminQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.adminContratoAnualizado(id),
    queryFn: () => obtenerContratoPorId(id),
  });

export const useContratoAdmin = (id: string) =>
  useSuspenseQuery(contratoAdminQueryOptions(id));

/** Cuántas solicitudes referencian el contrato · para avisar al editar (solo si hay clave). */
export function useUsoContrato(clave: string | null) {
  return useQuery({
    queryKey: [...qk.adminContratosAnualizados, "uso", clave],
    queryFn: () => obtenerUsoContrato(clave!),
    enabled: !!clave,
  });
}

export function useCrearContrato(opts?: { onOk?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clave, input }: { clave: string; input: ContratoAnualizadoInput }) =>
      crearContrato(clave, input),
    onSuccess: (ca) => {
      toastExito({ title: `Contrato ${ca.clave} creado`, message: "Guardado." });
      void qc.invalidateQueries({ queryKey: qk.adminContratosAnualizados });
      opts?.onOk?.();
    },
    onError: toastErrorApi,
  });
}

export function useActualizarContrato(opts?: { onOk?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clave, input }: { clave: string; input: ContratoAnualizadoInput }) =>
      actualizarContrato(clave, input),
    onSuccess: (ca) => {
      toastExito({ title: `Contrato ${ca.clave} actualizado`, message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminContratosAnualizados });
      void qc.invalidateQueries({ queryKey: qk.adminContratoAnualizado(ca.clave) });
      opts?.onOk?.();
    },
    onError: toastErrorApi,
  });
}

export function useDarDeBajaContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => darDeBajaContrato(id),
    onSuccess: () => {
      toastExito({
        title: "Contrato dado de baja",
        message: "Ya no se puede usar en solicitudes nuevas · se conserva el histórico.",
      });
      void qc.invalidateQueries({ queryKey: qk.adminContratosAnualizados });
    },
    onError: toastErrorApi,
  });
}

export function useReactivarContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivarContrato(id),
    onSuccess: () => {
      toastExito({ title: "Contrato reactivado", message: "Disponible de nuevo para solicitudes." });
      void qc.invalidateQueries({ queryKey: qk.adminContratosAnualizados });
    },
    onError: toastErrorApi,
  });
}
